// src/pages/users/UserDetail.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon,
  Avatar,
  Button,
  Tabs,
  Tab,
  Chip,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  CircularProgress,
  Badge,
  Stack,
  Alert,
  Skeleton,
  LinearProgress
} from '@mui/material';
import { 
  Person as PersonIcon, 
  Phone as PhoneIcon, 
  Email as EmailIcon,
  Home as HomeIcon,
  ShoppingCart as ShoppingCartIcon,
  School as SchoolIcon,
  ChildCare as ChildCareIcon,
  CalendarToday as CalendarTodayIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  MarkEmailRead as MarkEmailReadIcon,
  MarkEmailUnread as MarkEmailUnreadIcon
} from '@mui/icons-material';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase';

// Paleta de colores verde pastel
const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  success: '#7ED321',
  warning: '#F5A623',
  error: '#E74C3C',
  background: '#F8FDF8',
  surface: '#FFFFFF',
  text: {
    primary: '#2C3E50',
    secondary: '#7F8C8D'
  },
  status: {
    read: '#27AE60',
    unread: '#E74C3C',
    active: '#7ED321',
    inactive: '#BDC3C7'
  }
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [children, setChildren] = useState([]);
  const [orders, setOrders] = useState([]);
  const [serviceEnrollments, setServiceEnrollments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [readReceipts, setReadReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState(null);

  const fetchUserData = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch user details
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        setError('Usuario no encontrado');
        navigate('/admin/users');
        return;
      }
      
      const userData = { id: userDoc.id, ...userDoc.data() };
      setUser(userData);

      // Fetch all related data in parallel
      const [
        childrenSnapshot,
        ordersSnapshot,
        serviceEnrollmentsSnapshot,
        notificationsSnapshot,
        readReceiptsSnapshot
      ] = await Promise.all([
        // Children
        getDocs(query(
          collection(db, 'children'),
          where('parentId', '==', userId)
        )),
        // Orders
        getDocs(query(
          collection(db, 'orders'),
          where('userId', '==', userId),
          orderBy('createdAt', 'desc')
        )),
        // Service enrollments
        getDocs(query(
          collection(db, 'serviceEnrollments'),
          where('userId', '==', userId)
        )),
        // All notifications (to show user interaction)
        getDocs(query(
          collection(db, 'notifications'),
          orderBy('fecha', 'desc')
        )),
        // Read receipts for this user
        getDocs(query(
          collection(db, 'readReceipts'),
          where('userId', '==', userId)
        ))
      ]);

      // Process children
      const childrenList = childrenSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setChildren(childrenList);

      // Process orders
      const ordersList = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrders(ordersList);

      // Process notifications
      const notificationsList = notificationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setNotifications(notificationsList);

      // Process read receipts
      const readReceiptsList = readReceiptsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setReadReceipts(readReceiptsList);

      // Process service enrollments with service details
      const serviceEnrollmentsList = [];
      for (const docSnapshot of serviceEnrollmentsSnapshot.docs) {
        const enrollment = docSnapshot.data();
        try {
          const serviceDoc = await getDoc(doc(db, 'services', enrollment.serviceId));
          const serviceData = serviceDoc.exists() ? serviceDoc.data() : null;
          
          serviceEnrollmentsList.push({
            id: docSnapshot.id,
            ...enrollment,
            serviceName: serviceData?.nombre || 'Servicio no encontrado',
            serviceDetails: serviceData
          });
        } catch (error) {
          console.error(`Error fetching service ${enrollment.serviceId}:`, error);
          serviceEnrollmentsList.push({
            id: docSnapshot.id,
            ...enrollment,
            serviceName: 'Error al cargar servicio'
          });
        }
      }
      setServiceEnrollments(serviceEnrollmentsList);

    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Error al cargar los datos del usuario');
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchUserData();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [fetchUserData]);

  // Memoized calculations
  const notificationStats = useMemo(() => {
    const readNotificationIds = new Set(readReceipts.map(receipt => receipt.notificationId));
    
    const readCount = notifications.filter(notif => readNotificationIds.has(notif.id)).length;
    const unreadCount = notifications.length - readCount;
    const readPercentage = notifications.length > 0 ? Math.round((readCount / notifications.length) * 100) : 0;

    return {
      total: notifications.length,
      read: readCount,
      unread: unreadCount,
      readPercentage
    };
  }, [notifications, readReceipts]);

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return 'No disponible';
    
    try {
      const date = timestamp instanceof Date ? 
        timestamp : 
        new Date(timestamp.seconds * 1000);
        
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  }, []);

  const getOrderStatusText = useCallback((status) => {
    const statusMap = {
      pending: 'Pendiente',
      processing: 'En proceso',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    };
    return statusMap[status] || status || 'Desconocido';
  }, []);

  const getOrderStatusColor = useCallback((status) => {
    const colorMap = {
      pending: 'warning',
      processing: 'info',
      shipped: 'primary',
      delivered: 'success',
      cancelled: 'error'
    };
    return colorMap[status] || 'default';
  }, []);

  const isNotificationRead = useCallback((notificationId) => {
    return readReceipts.some(receipt => receipt.notificationId === notificationId);
  }, [readReceipts]);

  const getNotificationReadTime = useCallback((notificationId) => {
    const receipt = readReceipts.find(receipt => receipt.notificationId === notificationId);
    return receipt ? formatDate(receipt.readAt) : null;
  }, [readReceipts, formatDate]);

  const InfoCard = ({ icon, title, value, subtitle, color = COLORS.accent }) => (
    <Card sx={{ 
      height: '100%',
      background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
      border: `1px solid ${color}30`,
      transition: 'transform 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 25px ${color}20`
      }
    }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            backgroundColor: `${color}20`,
            color: color 
          }}>
            {icon}
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
              {value}
            </Typography>
            <Typography variant="body1" sx={{ color: COLORS.text.primary }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: COLORS.text.secondary }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ backgroundColor: COLORS.background, minHeight: '100vh', p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={300} height={40} />
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box sx={{ backgroundColor: COLORS.background, minHeight: '100vh', p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Usuario no encontrado'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/users')}
          variant="contained"
          sx={{
            backgroundColor: COLORS.accent,
            '&:hover': { backgroundColor: COLORS.secondary }
          }}
        >
          Volver a la lista de usuarios
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: COLORS.background, minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
          Detalle de Usuario
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/users')}
          variant="contained"
          sx={{
            backgroundColor: COLORS.accent,
            '&:hover': { backgroundColor: COLORS.secondary }
          }}
        >
          Volver
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* User Info Card */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ 
            p: 3, 
            height: '100%', 
            backgroundColor: COLORS.surface,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar 
                sx={{ 
                  width: 100, 
                  height: 100, 
                  mb: 2, 
                  bgcolor: COLORS.accent,
                  fontSize: '2rem',
                  fontWeight: 'bold'
                }}
              >
                {user.firstName?.charAt(0) || user.email?.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                ID: {userId}
              </Typography>
              <Chip 
                label={user.active ? "Activo" : "Inactivo"} 
                sx={{
                  backgroundColor: user.active ? COLORS.status.active + '20' : COLORS.status.inactive + '20',
                  color: user.active ? COLORS.status.active : COLORS.status.inactive,
                  fontWeight: 'bold',
                  border: `1px solid ${user.active ? COLORS.status.active : COLORS.status.inactive}30`
                }}
              />
            </Box>

            <Divider sx={{ mb: 3, backgroundColor: COLORS.primary }} />

            <List disablePadding>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <EmailIcon sx={{ color: COLORS.accent }} />
                </ListItemIcon>
                <ListItemText 
                  primary={<Typography variant="body1" fontWeight="bold">Email</Typography>}
                  secondary={user.email} 
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <PhoneIcon sx={{ color: COLORS.accent }} />
                </ListItemIcon>
                <ListItemText 
                  primary={<Typography variant="body1" fontWeight="bold">Teléfono</Typography>}
                  secondary={user.phone || 'No especificado'} 
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <HomeIcon sx={{ color: COLORS.accent }} />
                </ListItemIcon>
                <ListItemText 
                  primary={<Typography variant="body1" fontWeight="bold">Dirección</Typography>}
                  secondary={user.address || 'No especificada'} 
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <CalendarTodayIcon sx={{ color: COLORS.accent }} />
                </ListItemIcon>
                <ListItemText 
                  primary={<Typography variant="body1" fontWeight="bold">Fecha de registro</Typography>}
                  secondary={formatDate(user.createdAt)} 
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <ChildCareIcon sx={{ color: COLORS.accent }} />
                </ListItemIcon>
                <ListItemText 
                  primary={<Typography variant="body1" fontWeight="bold">Hijos registrados</Typography>}
                  secondary={children.length} 
                />
              </ListItem>
            </List>

            {/* Quick Stats */}
            <Divider sx={{ my: 3, backgroundColor: COLORS.primary }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2, backgroundColor: COLORS.background, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                    {orders.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pedidos
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2, backgroundColor: COLORS.background, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                    {serviceEnrollments.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Servicios
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Tabs and Content */}
        <Grid item xs={12} md={8} sx={{ display: 'flex', flexGrow: 1 }}>
          <Paper sx={{ 
            width: '100%', 
            backgroundColor: COLORS.surface,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: COLORS.accent,
                },
                '& .MuiTab-root': {
                  color: COLORS.text.secondary,
                  '&.Mui-selected': {
                    color: COLORS.accent,
                  },
                },
              }}
             >
              <Tab 
                label={
                  <Badge badgeContent={children.length} color="primary">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ChildCareIcon />
                      <span>Hijos</span>
                    </Box>
                  </Badge>
                } 
              />
              <Tab 
                label={
                  <Badge badgeContent={orders.length} color="primary">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ShoppingCartIcon />
                      <span>Pedidos</span>
                    </Box>
                  </Badge>
                } 
              />
              <Tab 
                label={
                  <Badge badgeContent={serviceEnrollments.length} color="primary">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SchoolIcon />
                      <span>Servicios</span>
                    </Box>
                  </Badge>
                } 
              />
              <Tab 
                label={
                  <Badge badgeContent={notificationStats.unread} color="error">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotificationsIcon />
                      <span>Notificaciones</span>
                    </Box>
                  </Badge>
                } 
              />
            </Tabs>

            {/* Children Tab */}
            <TabPanel value={tabValue} index={0}>
              {children.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <ChildCareIcon sx={{ fontSize: 64, color: COLORS.text.secondary, mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Este usuario no tiene hijos registrados
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {children.map((child) => (
                    <Grid item xs={12} sm={6} key={child.id}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          borderColor: COLORS.primary,
                          '&:hover': { 
                            borderColor: COLORS.accent,
                            boxShadow: `0 4px 12px ${COLORS.accent}20`
                          }
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ bgcolor: COLORS.secondary, mr: 2 }}>
                                {child.nombre?.charAt(0) || '?'}
                              </Avatar>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                  {child.nombre} {child.apellidos}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {child.curso || 'Curso no especificado'} - {child.clase || 'Clase no especificada'}
                                </Typography>
                              </Box>
                            </Box>
                            <Tooltip title="Ver detalles">
                              <IconButton 
                                onClick={() => navigate(`/admin/children/${child.id}`)}
                                sx={{ color: COLORS.accent }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </TabPanel>

            {/* Orders Tab */}
            <TabPanel value={tabValue} index={1}>
              {orders.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <ShoppingCartIcon sx={{ fontSize: 64, color: COLORS.text.secondary, mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Este usuario no tiene pedidos
                  </Typography>
                </Box>
              ) : (
                <List>
                  {orders.map((order) => (
                    <Paper 
                      key={order.id} 
                      sx={{ 
                        mb: 2, 
                        borderRadius: 2,
                        border: `1px solid ${COLORS.primary}30`,
                        '&:hover': { 
                          borderColor: COLORS.accent,
                          boxShadow: `0 4px 12px ${COLORS.accent}20`
                        }
                      }}
                    >
                      <ListItem
                        secondaryAction={
                          <Button 
                            variant="contained"
                            size="small"
                            onClick={() => navigate(`/admin/orders/${order.id}`)}
                            sx={{
                              backgroundColor: COLORS.accent,
                              '&:hover': { backgroundColor: COLORS.secondary }
                            }}
                          >
                            Ver detalles
                          </Button>
                        }
                      >
                        <ListItemIcon>
                          <ShoppingCartIcon sx={{ color: COLORS.accent }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Pedido #{order.id.substring(0, 6).toUpperCase()}
                              </Typography>
                              <Chip 
                                label={getOrderStatusText(order.status)} 
                                color={getOrderStatusColor(order.status)}
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <Stack spacing={0.5} sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                <strong>Fecha:</strong> {formatDate(order.createdAt)}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Total:</strong> {order.total?.toFixed(2) || '0.00'} €
                              </Typography>
                              <Typography variant="body2">
                                <strong>Productos:</strong> {order.items?.length || 0}
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItem>
                    </Paper>
                  ))}
                </List>
              )}
            </TabPanel>

            {/* Services Tab */}
            <TabPanel value={tabValue} index={2}>
              {serviceEnrollments.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <SchoolIcon sx={{ fontSize: 64, color: COLORS.text.secondary, mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Este usuario no está inscrito en ningún servicio
                  </Typography>
                </Box>
              ) : (
                <List>
                  {serviceEnrollments.map((enrollment) => (
                    <Paper 
                      key={enrollment.id} 
                      sx={{ 
                        mb: 2, 
                        borderRadius: 2,
                        border: `1px solid ${COLORS.primary}30`,
                        '&:hover': { 
                          borderColor: COLORS.accent,
                          boxShadow: `0 4px 12px ${COLORS.accent}20`
                        }
                      }}
                    >
                      <ListItem
                        secondaryAction={
                          <Button 
                            variant="contained"
                            size="small"
                            onClick={() => navigate(`/admin/services/${enrollment.serviceId}`)}
                            sx={{
                              backgroundColor: COLORS.accent,
                              '&:hover': { backgroundColor: COLORS.secondary }
                            }}
                          >
                            Ver servicio
                          </Button>
                        }
                      >
                        <ListItemIcon>
                          <SchoolIcon sx={{ color: COLORS.accent }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {enrollment.serviceName}
                            </Typography>
                          }
                          secondary={
                            <Stack spacing={0.5} sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                <strong>Fecha de inscripción:</strong> {formatDate(enrollment.enrollmentDate)}
                              </Typography>
                              {enrollment.childrenIds && enrollment.childrenIds.length > 0 && (
                                <Typography variant="body2">
                                  <strong>Hijos inscritos:</strong> {enrollment.childrenIds.length}
                                </Typography>
                              )}
                            </Stack>
                          }
                        />
                      </ListItem>
                    </Paper>
                  ))}
                </List>
              )}
            </TabPanel>

            {/* Notifications Tab */}
            <TabPanel value={tabValue} index={3}>
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <InfoCard
                      icon={<NotificationsIcon />}
                      title="Total"
                      value={notificationStats.total}
                      color={COLORS.accent}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoCard
                      icon={<CheckCircleIcon />}
                      title="Leídas"
                      value={notificationStats.read}
                      subtitle={`${notificationStats.readPercentage}% del total`}
                      color={COLORS.status.read}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoCard
                      icon={<ScheduleIcon />}
                      title="No leídas"
                      value={notificationStats.unread}
                      color={COLORS.status.unread}
                    />
                  </Grid>
                </Grid>

                {/* Progress bar */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Progreso de lectura: {notificationStats.readPercentage}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={notificationStats.readPercentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: COLORS.background,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: COLORS.status.read,
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              </Box>

              {notifications.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <NotificationsIcon sx={{ fontSize: 64, color: COLORS.text.secondary, mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No hay notificaciones en el sistema
                  </Typography>
                </Box>
              ) : (
                <List>
                  {notifications.map((notification) => {
                    const isRead = isNotificationRead(notification.id);
                    const readTime = getNotificationReadTime(notification.id);
                    
                    return (
                      <Paper 
                        key={notification.id} 
                        sx={{ 
                          mb: 2, 
                          borderRadius: 2,
                          border: `1px solid ${isRead ? COLORS.status.read : COLORS.status.unread}30`,
                          backgroundColor: isRead ? COLORS.status.read + '05' : COLORS.status.unread + '05',
                          '&:hover': { 
                            borderColor: isRead ? COLORS.status.read : COLORS.status.unread,
                            boxShadow: `0 4px 12px ${isRead ? COLORS.status.read : COLORS.status.unread}20`
                          }
                        }}
                      >
                        <ListItem
                          secondaryAction={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip 
                                icon={isRead ? <MarkEmailReadIcon fontSize="small" /> : <MarkEmailUnreadIcon fontSize="small" />}
                                label={isRead ? 'Leída' : 'No leída'}
                                size="small"
                                sx={{
                                  backgroundColor: isRead ? COLORS.status.read + '20' : COLORS.status.unread + '20',
                                  color: isRead ? COLORS.status.read : COLORS.status.unread,
                                  fontWeight: 'bold',
                                  border: `1px solid ${isRead ? COLORS.status.read : COLORS.status.unread}30`
                                }}
                              />
                              <Button 
                                variant="outlined"
                                size="small"
                                onClick={() => navigate(`/admin/notifications/${notification.id}`)}
                                sx={{
                                  borderColor: COLORS.accent,
                                  color: COLORS.accent,
                                  '&:hover': { 
                                    borderColor: COLORS.secondary,
                                    backgroundColor: COLORS.accent + '10'
                                  }
                                }}
                              >
                                Ver
                              </Button>
                            </Stack>
                          }
                        >
                          <ListItemIcon>
                            <Badge 
                              color={isRead ? "success" : "error"}
                              variant="dot"
                              sx={{
                                '& .MuiBadge-badge': {
                                  backgroundColor: isRead ? COLORS.status.read : COLORS.status.unread
                                }
                              }}
                            >
                              <NotificationsIcon sx={{ color: COLORS.accent }} />
                            </Badge>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: isRead ? 'normal' : 'bold' }}>
                                {notification.título}
                              </Typography>
                            }
                            secondary={
                              <Stack spacing={0.9} sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                  <strong>Fecha de envío:</strong> {formatDate(notification.fecha)}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Tipo:</strong> {notification.tipo || 'General'}
                                </Typography>
                                {isRead && readTime && (
                                  <Typography variant="body2" sx={{ color: COLORS.status.read }}>
                                    <strong>Leída el:</strong> {readTime}
                                  </Typography>
                                )}
                                {notification.mensaje && (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      mt: 1,
                                      fontStyle: 'italic',
                                      color: COLORS.text.secondary,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                    }}
                                  >
                                    {notification.mensaje}
                                  </Typography>
                                )}
                              </Stack>
                            }
                          />
                        </ListItem>
                      </Paper>
                    );
                  })}
                </List>
              )}
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserDetail;